<template>
  <div :class="$style['detail-info']">
    <div :class="$style.header">
      <w-managed-object-title :title="title" :sub-title="subTitle" :icon-class="objIconClass" :show-state="showOperState" :state="operStateValue" :clickable-title="!isClient" @title-click="routeForDetailInfo" />
    </div>
    <div :class="$style.body">
      <el-scrollbar>
        <div v-show="!isClient" v-loading="loading">
          <w-managed-object-basic-info-view class="mr-b15" ref="basicInfo" :object-info="obj" />
          <w-managed-object-perf-info-view class="mr-b15" ref="perfInfo" />
          <w-common-event-info-view ref="eventInfo" @click-detail="routeForDetailInfo" />
        </div>
        <div v-show="isClient" class="obj-major-info-content config-info">
          <div class="config-content-wrapper">
            <table class="config-content">
              <tr v-for="field in clientFields" :key="field.key">
                <th>{{ field.value }}</th>
                <td v-if="field.key === 'rssi'">
                  <i :class="getSignalStrengthIconClass(client.rssi)" />
                </td>
                <td v-else class="value">
                  {{ client[field.key] }}
                </td>
              </tr>
            </table>
          </div>
        </div>
      </el-scrollbar>
    </div>
  </div>
</template>

<script>
import { has, isEmpty, isNil } from 'lodash';
import { getValueAfterCheckEmpty, EMPTY_TEXT } from '@/utils/string';
import { convertValuByUnit } from '@/utils/number';
import { loadObjMajorInfo, getObjTypeIconClass, isVisibleOperStateOfObj, routeForDetailInfo, getObjStatusToBoolean } from '@/utils/obj-major-info';
import { getClientName, getClientConnectDurationString, getSignalStrengthIconClass } from '@/utils/wireless-device';
import WManagedObjectTitle from '@/views/topologymap/components/major-info/ManagedObjectTitle';
import WManagedObjectBasicInfoView from '@/views/topologymap/components/major-info/ManagedObjectBasicInfoView';
import WManagedObjectPerfInfoView from '@/views/topologymap/components/major-info/ManagedObjectPerfInfoView';
import WE2eAgentCombobox from '@/views/topologymap/components/major-info/components/E2eAgentCombobox';
import WCommonEventInfoView from '@/views/topologymap/components/major-info/CommonEventInfoView';
import MapConst from '@/constants/topologymap';

export default {
  name: 'WWirelessMapNodeDetailInfo',
  components: {
    WManagedObjectTitle,
    WManagedObjectBasicInfoView,
    WManagedObjectPerfInfoView,
    WCommonEventInfoView,
    WE2eAgentCombobox,
  },
  props: {
    data: {
      type: Object,
    },
  },
  data() {
    return {
      /**
       * 장비 정보
       * @type {Object}
       */
      obj: {},
      /**
       * 접속자 정보
       * @type {Object}
       */
      client: {},
      /**
       * 데이터 로딩 여부
       * @type {Boolean}
       */
      loading: false,
      /**
       * 운영상태 값
       * @type {Object}
       */
      operStateValue: null,
      /**
       * 접속자 필드 정보
       * @type {Array}
       */
      clientFields: [
        {
          key: 'ipAddress',
          value: 'IP',
        },
        {
          key: 'macAddress',
          value: 'MAC',
        },
        {
          key: 'companyName',
          value: '제조사',
        },
        {
          key: 'os',
          value: 'OS',
        },
        {
          key: 'ssid',
          value: 'WLAN',
        },
        {
          key: 'radioBand',
          value: '주파수 대역',
        },
        {
          key: 'rssi',
          value: '신호세기',
        },
        {
          key: 'traffic',
          value: '트래픽 IN/OUT',
        },
        {
          key: 'connectDuration',
          value: '접속 유지 시간',
        },
      ],
    };
  },
  computed: {
    /**
     * 운영상태 표현 여부
     * @return {Boolean}
     */
    showOperState() {
      return this.isClient ? false : isVisibleOperStateOfObj(this.obj.objDefineId);
    },
    /**
     * 타이틀
     * @author Ju
     * @return {String}
     */
    title() {
      return this.isClient ? this.client.name : getValueAfterCheckEmpty(this.obj.objName);
    },
    /**
     * 서브 타이틀
     * @author Ju
     * @return {String}
     */
    subTitle() {
      return this.isClient ? this.client.ipAddress : getValueAfterCheckEmpty(this.obj.ipaddress);
    },
    /**
     * 장비 아이콘 클래스
     * @author Ju
     * @return {String}
     */
    objIconClass() {
      if (this.isClient) {
        return 'fas fa-user';
      }
      return isEmpty(this.obj) ? null : getObjTypeIconClass(this.obj.objManagetypeId, this.obj.objDefineId, this.obj.objtypeId);
    },
    /**
     * 접속자 데이터 여부
     * @author Ju
     * @return {Boolean}
     */
    isClient() {
      return !has(this.data, 'objId');
    },
  },
  watch: {
    data: {
      immediate: true,
      handler(value) {
        this.reset();
        if (!isEmpty(value)) {
          if (this.isClient) {
            this.setClientFormData(value);
          } else {
            this.loadObjMajorInfo();
          }
        }
      },
    },
  },
  methods: {
    getSignalStrengthIconClass,
    /**
     * 접속자 폼 정보를 가공하여 설정한다.
     * @author Ju
     * @param  {[type]} data [description]
     */
    setClientFormData(data) {
      const clientInfo = data.clientInfo;
      this.client = {
        name: getClientName(clientInfo.clientName, clientInfo.ipAddress),
        ipAddress: getValueAfterCheckEmpty(clientInfo.ipAddress),
        macAddress: getValueAfterCheckEmpty(clientInfo.macAddress),
        companyName: getValueAfterCheckEmpty(clientInfo.companyName),
        os: getValueAfterCheckEmpty(clientInfo.os),
        ssid: getValueAfterCheckEmpty(clientInfo.ssid),
        radioBand: getValueAfterCheckEmpty(clientInfo.radioBand),
        rssi: getValueAfterCheckEmpty(clientInfo.rssi),
        traffic: `${isNil(clientInfo.trafficBpsIn) || clientInfo.trafficBpsIn === '' ? EMPTY_TEXT : convertValuByUnit(clientInfo.trafficBpsIn)} / ${
          isNil(clientInfo.trafficBpsOut) || clientInfo.trafficBpsOut === '' ? EMPTY_TEXT : convertValuByUnit(clientInfo.trafficBpsOut)
        }`,
        connectDuration: getClientConnectDurationString(clientInfo.connectDuration),
      };
    },
    /**
     * 장비 상세 정보를 조회한다.
     * @author Ju
     * @return {[type]} [description]
     */
    loadObjMajorInfo() {
      this.obj = this.data;
      const objDefineId = this.obj.objDefineId;
      let agentInfo = null;
      if (objDefineId === 'E2E_SERVICE') {
        agentInfo = {
          ...this.agentCommonInfo,
          objId: this.selectedAgentObjId,
        };
      }
      this.loading = true;
      loadObjMajorInfo(this.obj, agentInfo, true)
        .then((result) => {
          if (!isEmpty(result.majorInfoData)) {
            const majorInfoData = result.majorInfoData;
            this.$refs.basicInfo.setData(majorInfoData);
            this.$refs.perfInfo.setData(majorInfoData.perfInfo, objDefineId, this.obj.objtypeId, result.rscNameList);
            this.$refs.eventInfo.setData(majorInfoData.eventInfo, MapConst.EVENT_TYPE.STATUS, objDefineId);
            this.operStateValue = isEmpty(majorInfoData.stateValue) ? null : getObjStatusToBoolean(majorInfoData.stateValue[0].value, this.obj.objDefineId, this.obj.objtypeId);
          }
          const predictList = isEmpty(result.predictEventHist) ? [] : result.predictEventHist;
          this.$refs.eventInfo.setData(predictList, MapConst.EVENT_TYPE.PREDICT, objDefineId);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /**
     * 하위 컴포넌트 데이터 초기화한다.
     * @author Ju
     */
    reset() {
      Object.keys(this.$refs).forEach((key) => {
        if (!isEmpty(this.$refs[key])) {
          this.$refs[key].initData();
        }
      });
      this.operStateValue = null;
      this.obj = {};
      this.client = {};
    },
    /**
     * 상세 정보 화면으로 이동한다.
     * @author Ju
     * @param  {String} infoType
     */
    routeForDetailInfo(infoType) {
      routeForDetailInfo(this, this.obj, infoType, false);
    },
  },
};
</script>

<style lang="scss" module>
.detail-info {
  background: white;
  height: 100%;
  box-shadow: 1px 1px 3px #ddd;
  border: 1px solid #dadada;

  .header {
    height: 67px;
    padding: 15px;
    background: #eee;
    border-bottom: 1px solid #e0e0e0;
  }

  .body {
    padding: 15px;
    height: calc(100% - 67px);
  }
}
</style>



// WEBPACK FOOTER //
// src/views/performance/node/wireless-map/components/WirelessMapNodeDetailInfo.vue